// Copyright (c) Docugami, Inc. All rights reserved.

import React, { useEffect } from 'react';
import { LargeHeading, LargeUI } from 'components/common/Fonts';
import { Link } from '@fluentui/react';
import { DgVerticalSpacer } from 'components/common/DgCommonStyledComponents';
import StatusBar from 'components/file_management/status-bar';
import { DgUnauthenticatedShell } from './Header';
import styled from 'styled-components';
import { useOrgStatus } from 'hooks/OrgStatus';
import { Telemetry } from 'telemetry/TelemetryManager';
import { TelemetryPageView } from 'telemetry/TelemetryEvents';
import OpenedLetter from 'assets/OpenedLetter.png';

const Root = styled.div`
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    text-align: center;
`;

const StatusBarContainer = styled.div`
    width: 50%;
    height: 31px;
`

const TextContainer = styled.div`
    width: 40%;
`

export const OrganizationWaitingRoom = (): JSX.Element => {
    const status = useOrgStatus();

    useEffect(() => {
        if (status.currentOrgInitializing) {
            Telemetry.trackPageView(TelemetryPageView.CreatingWorkspace);
        }
        else if (status.waitlisted) {
            Telemetry.trackPageView(TelemetryPageView.CreateWorkspaceWaitlisted);
        }
    }, [status]);

    function OrgCreating(): JSX.Element {

        return (
            <Content>
                <img src={OpenedLetter} alt='Opened Letter' />
                <DgVerticalSpacer height='44px' />
                <LargeHeading>Creating your workspace...</LargeHeading>
                <DgVerticalSpacer height='44px' />
                <StatusBarContainer>
                    <StatusBar isInfinite={true} />
                </StatusBarContainer>
                <TextContainer>
                    <DgVerticalSpacer height='44px' />
                    <LargeUI style={{ fontWeight: 300 }}>
                    Your account will be ready soon.
                    </LargeUI>
                    <DgVerticalSpacer height='44px' />
                    <LargeUI style={{ fontWeight: 300 }}>
                        If you have any questions, please feel free to contact us through our <Link href="http://docugami.com/support" target='_blank'>Support Center</Link> or by directly emailing <Link href="mailto:support@docugami.com" target='_blank'>support@docugami.com</Link>
                    </LargeUI>
                </TextContainer>
            </Content >);
    }

    function Waitlisted(): JSX.Element {

        return (
            <Content>
                <LargeHeading>You’re on the list!</LargeHeading>
                <DgVerticalSpacer height='22px' />
                <LargeUI style={{ fontWeight: 300 }}>Due to high interest, we are currently limiting the number of new workspaces we are creating each day, to help ensure the best customer experience and support for everyone.</LargeUI>
                <DgVerticalSpacer height='15px' />
                <LargeUI style={{ fontWeight: 300 }}>We will create your workspace within the next week, and you will receive an email when it is ready.</LargeUI>
                <DgVerticalSpacer height='15px' />
                <LargeUI style={{ fontWeight: 300 }}>If you have any questions, please feel free to contact us through our <Link href="http://docugami.com/support" target='_blank'>Support Center</Link> or by directly emailing <Link href="mailto:support@docugami.com" target='_blank'>support@docugami.com</Link></LargeUI>
            </Content>);
    }

    return (
        <DgUnauthenticatedShell>
            <Root>
                {status.currentOrgInitializing === true ? OrgCreating() : status.waitlisted ? Waitlisted() : <></>}
            </Root>
        </DgUnauthenticatedShell>
    );
}

export default OrganizationWaitingRoom;
